import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../../common/types';
import { CatalogSong } from '../../../../../redux/api/fymAPI.types';
import { catalogSongMessages } from '../catalogSongMessages';
import { formatSongLength } from '../masterFile';
import { masterFailReasonMessage } from '../masterFailReason';
import { MasterPlayButton } from './MasterPlayButton';
import { MasterPlayerBar } from './MasterPlayerBar';
import { MasterDownloadButton } from './MasterDownloadButton';
import { masterFileType } from './masterPlayerFormat';
import { masterPlayerMessages } from './masterPlayerMessages';
import { useMasterPlayer } from './useMasterPlayer';
import { withBrandWord } from '../../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import songStyles from '../SongFiles.module.scss';
import styles from './MasterPlayer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MasterFileWithPlayerProps {
  accountId: number;
  itemUuid: string;
  master: NonNullable<CatalogSong['master']>;
  /** False when another part of the screen holds the player (the song page bar). */
  showPlayer?: boolean;
  /** Undefined: no Replace (the demo artist). */
  onReplace?: () => void;
}

/**
 * The master field of the song files sheet (design release-kit o2): the play button, the file
 * with its size, length, BPM and hook, the bar, then Download with the file type and size, and
 * Replace. While the AAC copy is not ready, the player is grey and Download works. A failed master
 * has no player: it shows why it failed, Download and Replace.
 */
const MasterFileWithPlayerComponent: React.FC<MasterFileWithPlayerProps> = ({
  accountId,
  itemUuid,
  master,
  showPlayer = true,
  onReplace,
}) => {
  const intl = useIntl();
  const player = useMasterPlayer({ accountId, itemUuid });
  const isFailed = master.status === 'failed';
  const canPlay = master.canPlay ?? master.status === 'ready';
  const sizeMb = intl.formatNumber(master.sizeBytes / (1024 * 1024), { maximumFractionDigits: 1 });

  const facts = [intl.formatMessage(catalogSongMessages.sizeMb, { size: sizeMb })];
  if (master.durationSec !== null) {
    facts.push(formatSongLength(master.durationSec));
  }
  if (master.bpm !== null) {
    facts.push(intl.formatMessage(catalogSongMessages.bpm, { bpm: Math.round(master.bpm) }));
  }
  if (master.hookStartSec !== null) {
    facts.push(intl.formatMessage(catalogSongMessages.hookAt, { time: formatSongLength(master.hookStartSec) }));
  }
  const type = masterFileType(master.name);

  return (
    <div className={`${songStyles.file} flex-column align-items-stretch gap-3`}>
      <div className={styles.row}>
        {isFailed || !showPlayer ? (
          <span className={`${songStyles.fileIcon} ${isFailed ? songStyles.fileIconFailed : ''}`}>
            <FontAwesomeIcon icon={isFailed ? faXmark : faCheck} />
          </span>
        ) : (
          <MasterPlayButton status={player.status} disabled={!canPlay} small onToggle={player.toggle} />
        )}
        <div className={songStyles.grow}>
          <span className={songStyles.strong}>{master.name}</span>
          <span className={songStyles.mono}>{facts.join(' · ')}</span>
          {isFailed && (
            <span className={songStyles.error}>
              {withBrandWord(
                intl.formatMessage(masterFailReasonMessage(master.failReason) ?? catalogSongMessages.masterFailed)
              )}
            </span>
          )}
        </div>
      </div>
      {!isFailed && showPlayer && (
        <MasterPlayerBar
          positionSec={player.positionSec}
          durationSec={player.durationSec ?? master.durationSec}
          hookStartSec={master.hookStartSec}
          disabled={!canPlay}
          onSeek={player.seek}
        />
      )}
      {!isFailed && !canPlay && (
        <span className={songStyles.small}>
          {withBrandWord(intl.formatMessage(masterPlayerMessages.processingDownload))}
        </span>
      )}
      <div className="d-flex align-items-center gap-3">
        <MasterDownloadButton accountId={accountId} itemUuid={itemUuid} fileName={master.name} />
        <span className={styles.fileMeta}>
          {type === ''
            ? intl.formatMessage(catalogSongMessages.sizeMb, { size: sizeMb })
            : intl.formatMessage(masterPlayerMessages.fileMeta, { type, size: sizeMb })}
        </span>
        <span className="flex-grow-1" />
        {onReplace !== undefined && (
          <button type="button" className={songStyles.linkButton} onClick={onReplace}>
            {intl.formatMessage(catalogSongMessages.replace)}
          </button>
        )}
      </div>
    </div>
  );
};

export const MasterFileWithPlayer = TypedMemo(MasterFileWithPlayerComponent);
